// src/modules/communications/components/SmsComposer.js
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaPaperPlane, FaSpinner, FaImage, FaTimes } from 'react-icons/fa';
import { useCommunication } from '../context/CommunicationContext';
import { formatPhoneNumber } from '../utils/formatUtils';
import Modal from '../../../components/shared/Modal';
import FeedbackModal from '../../../components/shared/ModalNotification';
import { compressImage, MMS_OPTIONS } from '../../../utils/imageCompression';

// Matches the multer limit on the API's MMS upload route.
const MAX_MMS_BYTES = 5 * 1024 * 1024;

const MessageTextarea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing(2)};
  margin: ${props => props.theme.spacing(2)} 0;
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  font-size: ${props => props.theme.typography.fontSize.medium};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    outline: none;
  }
`;

const MessageCounter = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.charCount > 240 ? props.theme.colors.error : props.theme.colors.textSecondary};
  text-align: right;
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const RecipientInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(2)};

  .phone {
    font-size: ${props => props.theme.typography.fontSize.large};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    color: ${props => props.theme.colors.textPrimary};
  }

  .name {
    margin-left: ${props => props.theme.spacing(1)};
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(2)};
`;

const AttachBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 13px;
  cursor: pointer;
  &:hover { background: ${props => props.theme.colors.inputGray}; color: ${props => props.theme.colors.textPrimary}; }
  &:disabled { opacity: 0.6; cursor: default; }
  .spinning { animation: spin 1s linear infinite; }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

const ImagePreview = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.inputGray};
  img { display: block; max-width: 200px; max-height: 150px; object-fit: cover; }
`;

const RemoveImg = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0,0,0,0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing(2)};
`;

const SendButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(3)};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.black};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-2px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  svg { font-size: 1rem; }
  .spinning { animation: spin 1s linear infinite; }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

const CancelButton = styled.button`
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(3)};
  background-color: transparent;
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  &:hover { background-color: ${props => props.theme.colors.surface}; }
`;

const SmsComposer = () => {
  const {
    smsComposerOpen,
    closeSmsComposer,
    smsRecipient,
    sendSmsMessage
  } = useCommunication();

  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [preparingMedia, setPreparingMedia] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const fileInputRef = useRef(null);
  const charCount = message.length;

  useEffect(() => {
    setMessage('');
    setMediaFile(null);
    setMediaPreview(null);
  }, [smsRecipient]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    // Shrink the photo first, then check the size — a phone camera shot is
    // always over the limit as-is, but is well under it once resized.
    setPreparingMedia(true);
    try {
      const prepared = await compressImage(file, MMS_OPTIONS);
      if (prepared.size > MAX_MMS_BYTES) {
        setFeedback('Image is too large to send, even after compression');
        return;
      }
      setMediaFile(prepared);
      setMediaPreview(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(prepared);
      });
    } finally {
      setPreparingMedia(false);
    }
  };

  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreview(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSend = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await sendSmsMessage(message, mediaFile);
      setMessage('');
      removeMedia();
      closeSmsComposer();
    } catch (error) {
      console.error('Error sending SMS:', error);
    } finally {
      setSending(false);
    }
  };

  if (!smsComposerOpen) return null;

  return (
    <Modal
      title={mediaFile ? "Send MMS" : "Send SMS"}
      isOpen={smsComposerOpen}
      onCancel={closeSmsComposer}
      onConfirm={null}
    >
      <RecipientInfo>
        <span className="phone">{formatPhoneNumber(smsRecipient.phone)}</span>
        {smsRecipient.name && <span className="name">({smsRecipient.name})</span>}
      </RecipientInfo>

      <MessageTextarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="Type your message here..."
        disabled={sending}
        autoFocus
      />

      <ToolbarRow>
        <AttachBtn
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={preparingMedia}
        >
          {preparingMedia
            ? <><FaSpinner className="spinning" /> Preparing...</>
            : <><FaImage /> Attach Image</>}
        </AttachBtn>
        <MessageCounter charCount={charCount}>{charCount}/250</MessageCounter>
      </ToolbarRow>

      <input
        type="file"
        ref={fileInputRef}
        accept="image/jpeg,image/png,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {mediaPreview && (
        <ImagePreview>
          <img src={mediaPreview} alt="Attachment" />
          <RemoveImg onClick={removeMedia}><FaTimes /></RemoveImg>
        </ImagePreview>
      )}

      <ButtonContainer>
        <CancelButton onClick={closeSmsComposer} disabled={sending}>Cancel</CancelButton>
        <SendButton onClick={handleSend} disabled={!message.trim() || sending}>
          {sending ? (
            <><FaSpinner className="spinning" /> Sending...</>
          ) : (
            <><FaPaperPlane /> {mediaFile ? 'Send MMS' : 'Send Message'}</>
          )}
        </SendButton>
      </ButtonContainer>

      <FeedbackModal
        isOpen={!!feedback}
        success={false}
        message={feedback || ''}
        duration={3000}
        onComplete={() => setFeedback(null)}
      />
    </Modal>
  );
};

export default SmsComposer;
